/* eslint-disable @nx/enforce-module-boundaries */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, from, forkJoin, empty, of, take, switchMap, map, pluck, skipWhile, filter, throwError, catchError, tap } from 'rxjs';
import moment from 'moment';
import { environment } from 'apps/studio/src/environments/environment';
import { HttpClient, HttpEventType } from '@angular/common/http';
import { ClaimService } from './child-services/claim.service';
import { JobService } from './child-services/job.service';
import {
	getSelectedItemOne,
	getSelectedItemTwo,
	getAllInfo,
	getFullItemOne,
	BigFormService,
	getSubmissionData,
	getFullItemTwo,
	getCurrentUser,
	getAppointmentTypes,
	IndexedDbService,
	getData,
	ModalService
} from '@flexus/core';
import { NewJobPayload } from '../models/new-job.model';
import { inlineRecursiveMerge, transformCallLog, findName, getAllInfoIndex } from '@flexus/utilities';
import { UUID } from 'angular2-uuid';
import { Router } from '@angular/router';
import { buildCompleteMapper } from '../configs/create-claim/pgg-create-claim';

export interface StoredDocument {
	id: string;
	documentType: string;
	documentLabel: string;
	file: File;
	base64: string;
	thumbnail?: string | null;
	timestamp: number;
}

export interface UploadProgress {
	documentType: string;
	documentLabel: string;
	status: 'pending' | 'uploading' | 'success' | 'error';
	progress?: number;
	error?: string;
}

@Injectable()
export class PggService {
	internalAssessorJobStates = [
		96, // internal_assessment
		97 // internal_assessor_job_report_uploaded
	];
	lastCreatedJobId: number | null = null;
	lastCreatedClaimId: number | null = null;
	allowNextDeactivate = false;

	private toPositiveNumber(value: any): number | null {
		const n = Number(value);
		return Number.isFinite(n) && n > 0 ? n : null;
	}

	private extractJobIdFromResponse(response: any): number | null {
		const candidates = [
			response?.updatePayload?.jobs?.[0]?.id,
			response?.updatePayload?.job?.id,
			response?.updatePayload?.job_id,
			response?.payload?.jobs?.[0]?.id,
			response?.payload?.job?.id,
			response?.payload?.job_id,
			response?.job?.id,
			response?.job_id
		];

		for (const candidate of candidates) {
			const jobId = this.toPositiveNumber(candidate);
			if (jobId) return jobId;
		}
		return null;
	}

	private extractClaimIdFromResponse(response: any): number | null {
		const candidates = [
			response?.payload?.id,
			response?.payload?.claim_id,
			response?.updatePayload?.id,
			response?.updatePayload?.claim_id,
			response?.claim?.id,
			response?.claim_id
		];

		for (const candidate of candidates) {
			const claimId = this.toPositiveNumber(candidate);
			if (claimId) return claimId;
		}
		return null;
	}

	private extractJobIdFromClaim(claim: any): number | null {
		const candidates = [
			claim?.loan_information?.vouchers?.[0]?.job_id,
			claim?.loan_information?.vouchers?.[0]?.id,
			claim?.jobs?.[0]?.id,
			claim?.job?.id,
			claim?.job_id
		];

		for (const candidate of candidates) {
			const jobId = this.toPositiveNumber(candidate);
			if (jobId) return jobId;
		}
		return null;
	}

	private isLikelyNetworkError(error: any): boolean {
		if (window.navigator.onLine === false) return true;
		
		if (error.status === 0) return true;

		const msg = String(error?.message ?? '').toLowerCase();
		const name = String(error?.name ?? '').toLowerCase();
		const code = String(error?.code ?? '').toLowerCase();

		// keep these narrow; avoid matching generic "error" terms
		if (name.includes('timeout') || name.includes('timeout') || name.includes('timeouterror')) return true;
		if (msg.includes('err_network') || msg.includes('failed to fetch')) return true;
		if (code === 'etimedout' || code === 'econnreset') return true;

		// return (
		// 	error?.status === 0 ||
		// 	error?.name === 'HttpErrorResponse' ||
		// 	(error?.message && String(error.message).toLowerCase().includes('network')) ||
		// 	window.navigator.onLine === false
		// );
	}

	constructor(
		private store: Store<any>,
		private http: HttpClient,
		private claimService: ClaimService,
		private jobService: JobService,
		private bf: BigFormService,
		private indexedDbService: IndexedDbService,
		private modalsService: ModalService,
		private router: Router
	) {}

	getSelectedItemOne() {
		return this.store.select(getSelectedItemOne).pipe(
			filter(x => !!x),
			take(1)
		);
	}

	getSelectedItemTwo() {
		return this.store.select(getSelectedItemTwo).pipe(
			filter(x => !!x),
			take(1)
		);
	}

	getJobSP() {
		return this.getSelectedItemTwo().pipe(
			pluck('sp'),
			switchMap(sp => {
				return this.getInterestedParties().pipe(
					pluck('payload'),
					map((interested_parties: any[]) => ({ ...interested_parties.find(ip => ip.sp.id === sp) }))
				);
			}),
			take(1)
		);
	}

	getAppointSPSummary() {
		return this.getJobSP().pipe(
			map(interestedParty => {
				let res;
				switch (interestedParty.new_interested) {
					case -1: {
						res = 'Rejected';
						break;
					}
					case 0: {
						res = 'Ignored';
						break;
					}
					case 1: {
						res = 'Accepted';
						break;
					}
					case null: {
						res = 'No-Reply';
						break;
					}
				}

				return [
					{ 'Primary Contact': interestedParty.sp.contact_person },
					{ 'Primary Contact Number': interestedParty.sp.contact_primary },
					{ maven_id: interestedParty.sp.mid !== null ? interestedParty.sp.mid : 'Not On Maven' },
					{
						'response to job': res
					},
					{ 'Currently Awarded Jobs': interestedParty.awarded_today }
				];
			}),
			take(1)
		);
	}

	appointSP() {
		return this.getSelectedItemTwo().pipe(
			switchMap(jobItem => {
				const selected_sp = this.bf.bigForm.get('sp_selected_item')?.value;
				return this.jobService.manuallyAllocateJob({ jobId: jobItem.id, spId: selected_sp.id });
			})
		);
	}

	repingSP() {
		const appointment = this.bf.bigForm.get('appointmentData')?.value;
		const date = moment(appointment.appointmentDatePicker);
		date.hour(appointment.appointmentTimePicker.hour);
		date.minutes(appointment.appointmentTimePicker.minutes);

		const date_formatted = date.format('YYYY-MM-DDTHH:mm:ss');
		const appointment_formatted = [{ range_start: date_formatted, range_end: date_formatted, appointment_type_id: appointment.appointmentTime }];

		return this.getSelectedItemTwo().pipe(
			pluck('id'),
			switchMap(job_id => {
				return this.jobService.repingJob({ appointments: appointment_formatted, job_id: job_id });
			})
		);
	}

	getAllClaims() {
		return this.claimService.getAllClaims();
	}

	getClaimAppointments() {
		return this.getSelectedItemOne().pipe(
			switchMap(claim => {
				return this.claimService.getClaimAppointments({ claim_id: claim?.id });
			})
		);
	}

	getCurrentFullItemOne() {
		return this.getSelectedItemOne().pipe(
			take(1),
			switchMap(item => this.getFullItemOne(item?.id))
		);
	}

	getFullItemOne(id) {
		return this.claimService.getFullClaim(id);
	}

	getFullItemOneWithItemTwo(id) {
		return this.claimService.getFullClaimWithJobs(id);
	}

	getFullItemTwo(id) {
		return this.jobService.getFullJob(id);
	}

	getInterestedParties() {
		return this.getSelectedItemTwo().pipe(
			filter(Boolean),
			pluck('id'),
			switchMap(jobId => {
				return this.jobService.getInterestedParties({ job_id: jobId });
			})
		);
	}

	dealOutOfApp() {
		return this.getSelectedItemTwo().pipe(switchMap(itemTwo => this.jobService.updateJob({ job_id: itemTwo?.id, new_state: 76 })));
	}

	cancelJob() {
		return this.getSelectedItemTwo().pipe(
			switchMap(job => {
				return this.jobService.cancelJob({ job_id: job?.id, reason: this.bf.bigForm?.value?.cancelclaim_reason });
			})
		);
	}

	newJob(jobData: NewJobPayload) {
		return this.jobService.newJob(jobData);
	}

	assessorVisitSite() {
		return forkJoin([
			this.store.select(getFullItemTwo).pipe(
				filter(Boolean),
				take(1),
			),
			this.store.select(getAllInfo).pipe(
				filter(Boolean),
				take(1),
				map(res => res?.appointment_types as any[]),
				
			)
		]).pipe(
			switchMap(([job, appointment_types]) => {
				let new_state;
				let return_to_state;
				console.log('job', job, 'app', appointment_types);
				switch (job.state) {
					case 106:
					case 32:
						new_state = job?.state;
						return_to_state = 90;
						break;
					case 90:
						new_state = 90;
						return_to_state = job?.job_information?.return_to_state;
						break;
					default:
						new_state = 90;
						if (job.state in [32, 90, 106]) {
							return_to_state = 89;
						} else {
							return_to_state = job?.state;
						}
				}

				const appointmentData: any = this.bf.bigForm.get('appointmentData')?.value;
				const date = appointmentData.appointmentDatePicker ? moment(appointmentData.appointmentDatePicker) : null;

				const range_start = moment(
					new Date(date.year(), date.month(), date.date(), appointmentData.appointmentTimePicker.hour, appointmentData.appointmentTimePicker.minutes)
				).format('YYYY-MM-DDTHH:mm:ss');

				const data = {
					appointment: {
						appointment_type: appointmentData.appointmentTime ? appointment_types.find(type => type.id === appointmentData.appointmentTime).id : null,
						range_start: range_start,
						reason: ''
					},
					current_state: job?.state,
					job_id: job?.id,
					job_information: {
						...job.job_information,
						return_to_state: return_to_state
					},
					new_state: new_state
				};

				return this.jobService.updateJob(data);
			})
		);
	}

	updateUnmanagedMavenJob() {
		return forkJoin([
			this.store.select(getSubmissionData).pipe(
				skipWhile(x => !x || !x.job_information),
				take(1)
			),
			this.store.select(getFullItemTwo).pipe(take(1))
		]).pipe(
			map(([submissionData, job]) => {
				const job_information = { ...job.job_information, ...submissionData.job_information };
				if (!job_information.OutAppFlowSPid && !job_information.OutAppFlowSPMID) {
					job_information.OutAppFlowSPid = 0;
					job_information.OutAppFlowSPMID = 0;
				}
				return { job_id: job?.id, new_state: submissionData.new_state, job_information: job_information };
			}),
			switchMap(data => {
				return this.jobService.updateJob(data);
			})
		);
	}

	generateBOQReport() {
		return this.store
			.select(getFullItemTwo)
			.pipe(take(1))
			.pipe(
				switchMap(response => {
					const { id } = response;
					return this.jobService.generateBOQReport(id);
				})
			);
	}

	updateJob() {
		return forkJoin([this.store.select(getSubmissionData).pipe(take(1)), this.store.select(getFullItemTwo).pipe(take(1))]).pipe(
			map(([submissionData, job]) => {
				const { job_information } = job;
				const { job_information: ji, ...rest } = submissionData;
				const result = JSON.parse(JSON.stringify(job_information ?? {}));
				inlineRecursiveMerge(result, ji);

				const response = {
					job_information: result,
					...rest,
					job
				};
				return response;
			}),
			switchMap(response => {
				const { job, ...data } = response;
				return this.jobService.updateJob({ job_id: job?.id, ...data });
			})
		);
	}

	updateClaim() {
		return forkJoin([this.store.select(getSubmissionData).pipe(take(1)), this.store.select(getFullItemOne).pipe(take(1))]).pipe(
			map(([submissionData, claim]) => {
				const { loan_information } = claim;
				const { loan_information: ci, ...rest } = submissionData;
				const result = JSON.parse(JSON.stringify(loan_information));
				inlineRecursiveMerge(result, ci);

				const response = {
					loan_information: result,
					...rest,
					claim
				};
				return response;
			}),
			switchMap(response => {
				const { claim, ...data } = response;
				return this.claimService.updateClaim({ claim_id: claim?.id, ...data });
			})
		);
	}

	createClaim() {
		return this.store.select(getSubmissionData).pipe(
			take(1),
			switchMap(claim => {
				// console.log('createClaim 3434', claim);
				return this.claimService.createClaim({ ...claim }).pipe(
					tap((res: any) => {
						const claimId = this.extractClaimIdFromResponse(res);
						if (claimId) {
							this.lastCreatedClaimId = claimId;
						}

						const jobId = res?.updatePayload?.jobs?.[0]?.id;
						if (jobId) {
							this.lastCreatedJobId = jobId;
						}
					})
				);
			})
		);
	}

	submitClaimWithOfflineFallback() {
		if (!window.navigator.onLine) {
			this.allowNextDeactivate = false;
			return this.saveClaimLocallyAfterSubmitFailure();
		}
	
		return this.createClaimWithJobImageUpload().pipe(
			catchError(error => {
				if (this.isLikelyNetworkError(error)) {
					this.allowNextDeactivate = false;
					return this.saveClaimLocallyAfterSubmitFailure();
				}
	
				return throwError(() => error);
			})
		);
	}

	createClaimWithJobImageUpload() {
		// Mark this navigation path as submit-driven so deactivate guard can allow one transition.
		this.allowNextDeactivate = true;
		return this.store.select(getSubmissionData).pipe(
			take(1),
			switchMap(claim => {
				return this.claimService.createClaim({ ...claim }, { skipPostCreateUpdate: true }).pipe(
					tap((res: any) => {
						const claimId = this.extractClaimIdFromResponse(res);
						if (claimId) {
							this.lastCreatedClaimId = claimId;
						}

						const jobId = this.extractJobIdFromResponse(res);
						if (jobId) {
							this.lastCreatedJobId = jobId;
						}
					}),
					switchMap((createRes: any) => {
						const claimId = this.extractClaimIdFromResponse(createRes);
						if (!claimId) {
							return throwError(() => new Error('No claim id returned from create claim response'));
						}

						const jobId = this.extractJobIdFromResponse(createRes);
						return this.uploadSupportingDocumentsWithClaimId(claimId, jobId ?? undefined).pipe(
							switchMap(uploadResult => {
								const updatePayload = { ...claim, claim_id: claimId, new_state: 138 };
								return this.claimService.updateClaim(updatePayload).pipe(
									tap((updateRes: any) => {
										const jobId = this.extractJobIdFromResponse({ updatePayload: updateRes?.payload });
										if (jobId) {
											this.lastCreatedJobId = jobId;
										}
									}),
									switchMap((updateRes: any) =>
										from(this.indexedDbService.currentItem.delete('currentItem')).pipe(
											// Ensure deactivate guard sees a clean state after successful submit
											tap(() => {
												this.bf?.bigForm?.markAsPristine?.();
												this.bf?.bigForm?.markAsUntouched?.();
											}),
											map(() => ({
												...createRes,
												updatePayload: updateRes?.payload,
												uploadPayload: uploadResult
											})),
											catchError(() => {
												// Don't fail successful submit if local cleanup fails
												this.bf?.bigForm?.markAsPristine?.();
												this.bf?.bigForm?.markAsUntouched?.();
												return of({
													...createRes,
													updatePayload: updateRes?.payload,
													uploadPayload: uploadResult
												});
											})
										)
									)
								);
							})
						);
					})
				);
			})
			,
			catchError(error => {
				// Reset bypass flag on submit failure.
				this.allowNextDeactivate = false;
				return throwError(() => error);
			})
		);
	}

	cancelClaim() {
		return this.getSelectedItemOne().pipe(
			switchMap(claim => {
				return this.claimService.cancelClaim({
					claim_id: claim?.id,
					reason: this.bf.bigForm?.value?.cancel_reason
				});
				// return this.claimService.cancelClaim({claim_id: claim?.id, reason: this.bf.bigForm.value.cancel_reason})
			})
		);
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	reopenClaim(data: any) {
		return this.store
			.select(getFullItemOne)
			.pipe(
				filter(Boolean),
				take(1),)
			.pipe(
				map(claim => {
					const claim_id = (claim as any).id;
					const reason = this.bf.bigForm.get('reopen_data');

					return {
						claim_id: claim_id,
						reopen_code: reason?.value?.reopen_reason
					};
				}),
				switchMap(response => {
					return this.claimService.reopenClaim(response);
				})
			);
	}

	saveAsServerDraft() {
		return this.store.select(getSubmissionData).pipe(
			take(1),
			switchMap(claim => {
				// IMPORTANT: saving as a server draft must ONLY hit create_claim (new_state: 8)
				// and must never trigger the post-create update_claim call.
				return this.claimService.createClaimDraft({ ...claim });
			})
		);
	}

	checkNetworkAndUpdateDraft() {
		if (window.navigator.onLine) {
			// update server draft
			return this.saveAsServerDraft();
		} else {
			return this.saveAsLocalDraft();
		}
	}

	updateServerDraft(new_state) {
		return forkJoin([this.store.select(getSubmissionData).pipe(take(1)), this.store.select(getFullItemOne).pipe(take(1))]).pipe(
			map(([submissionData, claim]) => {
				const { loan_information } = claim;
				const { loan_information: ci, ...rest } = submissionData;
				const result = JSON.parse(JSON.stringify(loan_information));
				inlineRecursiveMerge(result, ci);
				const lockedTenantId = claim?.tenant_id ?? claim?.source_id ?? null;

				const response = {
					loan_information: result,
					...rest,
					// Edit-flow must never mutate tenant/source on draft updates.
					...(lockedTenantId != null ? { tenant_id: lockedTenantId } : {}),
					claim
				};
				// console.log({ claim });
				return response;
			}),
			switchMap(response => {
				const { claim, ...data } = response;
				return this.claimService.updateClaim({ claim_id: claim?.id, ...data, new_state }).pipe(
					tap((res: any) => {
						const resolvedJobId = this.extractJobIdFromResponse(res) ?? this.extractJobIdFromClaim(claim);
						if (resolvedJobId) {
							this.lastCreatedJobId = resolvedJobId;
						}
					})
				);
			})
		);
	}

	updateServerDraftAsSubmitted() {
		return forkJoin([this.store.select(getSubmissionData).pipe(take(1)), this.store.select(getFullItemOne).pipe(take(1))]).pipe(
			map(([submissionData, claim]) => {
				const { loan_information } = claim;
				const { loan_information: ci, new_state, ...rest } = submissionData;
				const result = JSON.parse(JSON.stringify(loan_information));
				inlineRecursiveMerge(result, ci);
				const lockedTenantId = claim?.tenant_id ?? claim?.source_id ?? null;

				const response = {
					loan_information: result,
					...rest,
					// Edit-flow must never mutate tenant/source on submit updates either.
					...(lockedTenantId != null ? { tenant_id: lockedTenantId } : {}),
					claim,
					new_state
				};
				return response;
			}),
			switchMap(response => {
				const { claim, new_state, ...data } = response;
				return this.claimService.updateClaim({ claim_id: claim?.id, ...data, new_state: new_state }).pipe(
					tap((res: any) => {
						const resolvedJobId = this.extractJobIdFromResponse(res) ?? this.extractJobIdFromClaim(claim);
						if (resolvedJobId) {
							this.lastCreatedJobId = resolvedJobId;
						}
					})
				);
			})
		);
	}

	/**
	 * Persists the current claim as a local draft (state 169).
	 * Prefer the live IndexedDB `currentItem` snapshot because it is the freshest copy
	 * of the in-progress form. Fall back to store submissionData if `currentItem`
	 * is unavailable, and preserve any existing tempKey when editing a local draft.
	 */
	saveAsLocalDraft() {
		return forkJoin([
			this.bf.bigFormToStoreMapper(buildCompleteMapper(this.bf)).pipe(take(1), catchError(() => of(null))),
			from(this.indexedDbService.currentItem.get('currentItem')).pipe(catchError(() => of(null))),
			this.store.select(getSubmissionData).pipe(take(1)),
			this.store.select(getSelectedItemOne).pipe(take(1), catchError(() => of(null))),
			this.store.select(getFullItemOne).pipe(take(1), catchError(() => of(null)))
		]).pipe(
			switchMap(([liveMappedDraft, currentItem, submissionData, selectedItemOne, fullItemOne]) => {
				const rawFormValue = this.bf?.bigForm?.getRawValue?.() ?? this.bf?.bigForm?.value ?? {};
				const draftSource = JSON.parse(JSON.stringify(submissionData ?? {}));
				if (liveMappedDraft && typeof liveMappedDraft === 'object') {
					inlineRecursiveMerge(draftSource, liveMappedDraft);
				}
				if (currentItem && typeof currentItem === 'object') {
					inlineRecursiveMerge(draftSource, currentItem);
				}
				if (Array.isArray(rawFormValue?.jobs)) {
					draftSource.jobs = JSON.parse(JSON.stringify(rawFormValue.jobs));
				}
				[
					'checked_company_selection',
					'checked_supporting_document_upload',
					'checked_customer_details',
					'checked_address_confirmation',
					'checked_job_details',
					'checked_appointments'
				].forEach(flag => {
					if (rawFormValue?.[flag] !== undefined) {
						draftSource[flag] = rawFormValue[flag];
					}
				});
				const tempKey =
					draftSource?.tempKey ??
					rawFormValue?.tempKey ??
					currentItem?.tempKey ??
					selectedItemOne?.tempKey ??
					fullItemOne?.tempKey ??
					UUID.UUID();
				const serverClaimId = this.toPositiveNumber(
					draftSource?.id ?? fullItemOne?.id ?? selectedItemOne?.id ?? currentItem?.id
				);
				const localDraft = {
					...draftSource,
					tempKey,
					state: 169,
					...(serverClaimId ? { id: serverClaimId } : {})
				};
				return from(this.indexedDbService.claimInDraft.put(localDraft));
			})
		);
	}

	checkNetworkAndSaveDraft() {
		if (window.navigator.onLine) {
			return this.saveAsServerDraft();
		} else {
			return this.saveAsLocalDraft();
		}
	}
	private saveClaimLocallyAfterSubmitFailure() {
		return of(null).pipe(
			switchMap(() => {
				return new Observable(observer => {
					this.modalsService.openModalDirectly((inst: any) => {
						inst.type = 'warning';
						inst.heading = 'You are offline';
						inst.closeButton = false;
						inst.setMessage([
							'This claim cannot be submitted right now.',
							'Your progress will be saved locally so you can continue later when you are back online.'
						]);
						inst.navButtons = [
							{
								text: 'Save Locally',
								color: 'primary',
								linkType: 'close',
								clickHandler: () => {
									this.saveAsLocalDraft()
										.pipe(
											take(1),
											switchMap(localDraftResult =>
												this.deleteCurrentEditableItem().pipe(
													catchError(() => of({ success: false })),
													map(() => localDraftResult)
												)
											),
											switchMap(localDraftResult =>
												this.navigateToWorkflow().pipe(
													map(() => ({
														success: true,
														savedLocally: true,
														payload: localDraftResult
													}))
												)
											)
										)
										.subscribe({
											next: result => {
												this.allowNextDeactivate = true;
												observer.next(result);
												observer.complete();
											},
											error: error => observer.error(error)
										});
								}
							}
						];
					});
				});
			})
		);
	}
	private saveDraftLocallyWithOfflineModal() {
		return of(null).pipe(
			switchMap(() => {
				return new Observable(observer => {
					this.modalsService.openModalDirectly((inst: any) => {
						inst.type = 'warning';
						inst.heading = 'You are offline';
						inst.closeButton = false;
						inst.setMessage([
							'This draft cannot be saved to the server right now.',
							'Your progress will be saved locally so you can continue later when you are back online.'
						]);
						inst.navButtons = [
							{
								text: 'Save Locally',
								color: 'primary',
								linkType: 'close',
								clickHandler: () => {
									this.saveAsLocalDraft()
										.pipe(
											take(1),
											switchMap(localDraftResult =>
												this.deleteCurrentEditableItem().pipe(
													catchError(() => of({ success: false })),
													map(() => localDraftResult)
												)
											),
											switchMap(localDraftResult =>
												this.navigateToWorkflow().pipe(
													map(() => ({
														success: true,
														savedLocally: true,
														payload: localDraftResult
													}))
												)
											)
										)
										.subscribe({
											next: result => {
												this.allowNextDeactivate = true;
												observer.next(result);
												observer.complete();
											},
											error: error => observer.error(error)
										});
								}
							}
						];
					});
				});
			})
		);
	}

	promoteLocalToServerDraftandSubmitEdited(targetNewState: number = 138) {
			return forkJoin([
				this.store.select(getSubmissionData).pipe(take(1)),
				this.store.select(getFullItemOne).pipe(take(1)),
			]).pipe(
				switchMap(([submissiondata, selected]: any[]) => {
					const tempKey = selected?.tempKey;
					// craete server draft (state 8)
					return this.claimService.createClaimDraft({ ...(submissiondata ?? {}) }).pipe(
						switchMap((createRes: any) => {
							const serverClaimId = this.extractClaimIdFromResponse(createRes);
							if (!serverClaimId) {
								return throwError(() => new Error('Failed to create server draft'));
							}

							// prepare update payload similar to updateServerDraft(...)
							const createdLoanInformation = createRes?.payload?.loan_information ?? {};
							const { loan_information: submissionLoanInfo, ...rest} = submissiondata ?? {};
							const mergedLoadInfo = JSON.parse(JSON.stringify(createdLoanInformation ?? {}));
							inlineRecursiveMerge(mergedLoadInfo, submissionLoanInfo ?? {});
							const lockedTenantId = createRes?.payload?.tenant_id ?? createRes?.payload?.source_id ?? null;

							const updatePayload = {
								loan_information: mergedLoadInfo,
								...rest,
								...(lockedTenantId != null ? { tenant_id: lockedTenantId } : {}),
								claim: createRes?.payload
							};

							// update server draft
							return this.claimService.updateClaim({ claim_id: serverClaimId, ...updatePayload, new_state: targetNewState }).pipe(
								tap((updateRes: any) => {
									const resolvedJobid = this.extractJobIdFromResponse(updateRes) ?? this.extractJobIdFromClaim(createRes?.payload);
									if (resolvedJobid) {
										this.lastCreatedJobId = resolvedJobid;
									}
								}),
								switchMap((updateRes: any) => {
									// upload docs
									return this.uploadSupportingDocuments().pipe(
										switchMap((uploadResult) => {
											// clean up local draft + local draft
											return from(this.indexedDbService.currentItem.delete('currentItem')).pipe(
												switchMap(() => {
													if (tempKey) {
														return from(this.indexedDbService.claimInDraft.delete(tempKey)).pipe(
															catchError(() => of(null))
														);
													}
													return of(null)
												}),
												map(() => ({
													...updateRes,
													uploadPayload: uploadResult
												}))
											)
										})
									)
								})
							)
						})
					)
				})
			)
	}

	updateEditedDraft() {
		if (!window.navigator.onLine) {
			return this.saveDraftLocallyWithOfflineModal();
		}

		return this.store.select(getSelectedItemOne).pipe(
			take(1),
			switchMap((claim: any) => {
				const isLocalDraft = claim?.state === 169;
				const hasServerId = this.toPositiveNumber(claim?.id) != null;

				let update$;
				if (isLocalDraft && hasServerId) {
					update$ = this.updateServerDraft(8).pipe(
						switchMap(res => {
							const tempKey = claim?.tempKey;
							if (tempKey) {
								return from(this.indexedDbService.claimInDraft.delete(tempKey)).pipe(
									catchError(() => of(null)),
									map(() => res)
								);
							}
							return of(res);
						})
					);
				} else if (isLocalDraft) {
					update$ = this.promoteLocalToServerDraftandSubmitEdited(8);
				} else {
					update$ = this.updateServerDraft(8);
				}

				return update$;
			}),
			catchError(error => {
				if (this.isLikelyNetworkError(error)) {
					return this.saveDraftLocallyWithOfflineModal();
				}
				return throwError(() => error);
			})
		);
	}

	submitEditedClaimWithOfflineFallback() {
		if (!window.navigator.onLine) {
			this.allowNextDeactivate = false;
			return this.saveClaimLocallyAfterSubmitFailure();
		}

		this.allowNextDeactivate = true;
		return this.store.select(getSelectedItemOne).pipe(
			take(1),
			switchMap((claim: any) => {
				// discriminator
				const isLocalOnlyDraft = claim?.state === 169;
				const submitCall$ = isLocalOnlyDraft
					? this.promoteLocalToServerDraftandSubmitEdited()
					: this.updateServerDraft(138);

				return submitCall$.pipe(
					switchMap((submitRes: any) => {
						return this.uploadSupportingDocuments().pipe(
							switchMap(uploadResult => {
								return from(this.indexedDbService.currentItem.delete('currentItem')).pipe(
									tap(() => {
										this.bf?.bigForm?.markAsPristine?.();
										this.bf?.bigForm?.markAsUntouched?.();
									}),
									map(() => ({
										...(submitRes ?? {}),
										uploadPayload: uploadResult
									})),
									catchError(() => {
										this.bf?.bigForm?.markAsPristine?.();
										this.bf?.bigForm?.markAsUntouched?.();
										return of({
											...(submitRes ?? {}),
											uploadPayload: uploadResult
										});
									})
								);
							})
						);
					})
				);
			}),
			catchError(error => {
				if (this.isLikelyNetworkError(error)) {
					this.allowNextDeactivate = false;
					return this.saveClaimLocallyAfterSubmitFailure();
				}

				this.allowNextDeactivate = false;
				return throwError(() => error);
			})
		);
		// return this.updateServerDraft(138).pipe(
		// 	switchMap((updateRes: any) => {
		// 		return this.uploadSupportingDocuments().pipe(
		// 			switchMap(uploadResult =>
		// 				from(this.indexedDbService.currentItem.delete('currentItem')).pipe(
		// 					tap(() => {
		// 						this.bf?.bigForm?.markAsPristine?.();
		// 						this.bf?.bigForm?.markAsUntouched?.();
		// 					}),
		// 					map(() => ({
		// 						...updateRes,
		// 						uploadPayload: uploadResult
		// 					})),
		// 					catchError(() => {
		// 						this.bf?.bigForm?.markAsPristine?.();
		// 						this.bf?.bigForm?.markAsUntouched?.();
		// 						return of({
		// 							...updateRes,
		// 							uploadPayload: uploadResult
		// 						});
		// 					})
		// 				)
		// 			)
		// 		);
		// 	}),
		// 	catchError(error => {
		// 		if (this.isLikelyNetworkError(error)) {
		// 			this.allowNextDeactivate = false;
		// 			return this.saveClaimLocallyAfterSubmitFailure();
		// 		}

		// 		this.allowNextDeactivate = false;
		// 		return throwError(() => error);
		// 	})
		// );
	}

	deleteCurrentEditableItem() {
		return from(this.indexedDbService.currentItem.delete('currentItem')).pipe(map(res => ({ success: true, payload: res })));
	}

	deleteLocalDraft() {
		return this.getSelectedItemOne().pipe(switchMap(itemOne => from(this.indexedDbService.claimInDraft.delete(itemOne.tempKey))));
	}

	navigateToWorkflow() {
		return from(
			this.router.navigate(['/workflow'], { state: { flxBypassDeactivateGuard: true } })
		);
	}

	policyLookup(data: { search: string }) {
		return this.http.post(`${environment.api_url}v1/claim_action/get_policies/`, {
			search: data?.search
		});
	}

	policyHistorySearch(data: { search: string }) {
		return this.http.post(`${environment.api_url}v1/claim_action/get_claim_history/`, {
			search: data?.search
		});
	}

	getItemOneExtra() {
		return this.getSelectedItemOne().pipe(
			switchMap(claim => {
				return this.claimService.getClaimExtra(claim?.id);
			})
		);
	}

	getCloseClaimInfo() {
		return this.getSelectedItemOne().pipe(
			switchMap(claim => {
				return this.getCloseClaimInfoAPI({ claim_id: claim?.id }).pipe(
					map(res => {
						return res;
					})
				);
			})
		);
	}

	getSkills() {
		return this.store.select(getAllInfo).pipe(
			skipWhile((allInfo: any) => {
				return allInfo.states === null || allInfo.states[1] === undefined;
			})
		);
	}

	getAppointmentTypes() {
		return this.store.select(getAppointmentTypes).pipe(
			filter(Boolean)
		);
	}

	getActiveReions() {
		return this.store.select(this.getActiveReions).pipe(
			filter(Boolean)
		);
	}

	getJobInvoice() {
		return this.getSelectedItemTwo().pipe(
			filter(Boolean),
			take(1),
			switchMap((itemTwo: any) => {
				return this.http
					.post<any>(environment.api_url + `v1/file_action/get_invoice/`, {
						job_id: itemTwo?.id,
						return_type: 1
					})
					.pipe(map(payload => payload.payload));
			})
		);
	}

	getJobReport() {
		return this.getSelectedItemTwo().pipe(
			filter(Boolean),
			take(1),
			switchMap((itemTwo: any) => {
				return this.http
					.post<any>(environment.api_url + `v1/file_action/get_invoice/`, {
						job_id: itemTwo?.id,
						return_type: 1
					})
					.pipe(map(payload => payload.payload));
			})
		);
	}

	getServiceProvidersOnClaim() {
		return combineLatest([this.getSelectedItemOne(), this.store.select(getAllInfo).pipe(map((allInfo: any) => allInfo.sps))]).pipe(
			map(([claim, serviceProviders]) => {
				const jobs = claim?.jobs?.filter(job => job?.state !== 45 || job?.state !== 72);
				return jobs
					.filter(job => !!job.sp)
					.map(job => ({
						jobId: job?.id,
						skill: job?.skill,
						spName: findName(job?.sp, serviceProviders).name
					}));
			})
		);
	}

	getClaimMid() {
		return this.store.select(getFullItemOne).pipe(
			filter(Boolean),
			pluck('mid'),
			take(1),
			map(data => {
				return data;
			})
		);
	}

	getAllClaimFiles() {
		return this.getSelectedItemOne().pipe(
			switchMap(itemOne => {
				return this.getAllFilesApi(null, itemOne?.id, null);
			})
		);
	}

	getAllJobFiles() {
		return this.getSelectedItemTwo().pipe(
			switchMap(itemTwo => {
				if (itemTwo) {
					return this.getAllFilesApi(itemTwo.id, null);
				} else {
					return empty();
				}
			})
		);
	}

	getNotes() {
		return combineLatest([this.getSelectedItemTwo(), this.getInternalAssessorJob()]).pipe(
			switchMap(([itemTwo, iaJob]: any) => {
				if (itemTwo && iaJob && itemTwo?.id === iaJob.id) {
					return this.getjobAssessmentNotes();
				} else {
					return this.getJobNotes();
				}
			})
		);
	}

	getJobNotes() {
		return this.getSelectedItemTwo().pipe(
			switchMap(itemTwo => {
				if (itemTwo) {
					return this.getJobNotesApi(itemTwo.id);
				} else {
					return empty();
				}
			})
		);
	}

	getjobAssessmentNotes() {
		return this.getSelectedItemTwo().pipe(
			switchMap(itemTwo => {
				return this.getjobAssessmentNotesApi(itemTwo.id).pipe(
					filter(Boolean),
					take(1),
					map((data: any) => data.payload),
					// pluck('payload'),
					map(iaNotes => iaNotes.map(iaNote => ({ timestamp: iaNote.created, message: iaNote.note })))
				);
			})
		);
	}

	getInternalAssessorJob() {
		return this.store.select(getSelectedItemOne).pipe(
			take(1),
			map(itemOne => {
				if (itemOne) {
					const iaJob = this.getinternalAssessorJobCard(itemOne.jobs);
					return iaJob ? iaJob : { id: 0 };
				}
			})
		);
	}

	getPolicyHistory() {
		return this.store.select(getSelectedItemOne).pipe(
			filter(Boolean),
			take(1),
			switchMap(itemOne => {
				if (itemOne) {
					return this.policyHistoryLookup({
						claim_id: itemOne?.id,
						search: this.bf.bigForm?.value?.policy_number
					}).pipe(
						pluck('payload', 'claims'),
						map(claims => {
							if (claims) {
								return (claims as any[]).map(claim => {
									const obj = {
										date: { title: 'Loss Date', value: this.formatDate(claim?.LOSSDATE) },
										eventType: { title: 'Claim Type', value: claim?.CLAIMTYPE },
										additionalItems: [
											{ title: 'Status', value: claim?.STATUS },
											{ title: 'Claim Cost', value: `R${claim?.CLAIMCOST}` }
										],
										description: { title: 'Comment', value: claim?.COMMENTS }
									};
									return obj;
								});
							}
							return claims;
						}),
						map((claims: any[]) => {
							if (claims) {
								return { claims: claims.sort((a, b) => moment.utc(b.date.value)?.diff(a.date.value)) };
							}
							return { claims: claims };
						}),
						take(1)
					);
				} else {
					return empty();
				}
			}),
			map(res => res?.claims)
		);
	}

	formatDate(date: string): Date {
		return moment(date, 'MM-D-YYYY h mm ss a').toDate();
	}

	// TODO CLAIM SERVICES

	getCloseClaimInfoAPI(params: any): Observable<any> {
		return this.http.post(environment.api_url + `v1/claim_action/can_finalise_claim/`, params);
	}

	finaliseClaim(params: { claim_id: number }): Observable<any> {
		return this.http.post(environment.api_url + `v1/claim_action/finalise_claim/`, params);
	}

	policyHistoryLookup(data: { claim_id: string; search: string }) {
		return this.http.post(`${environment.api_url}v1/claim_action/get_claim_history/`, {
			search: data?.search || '',
			claim_id: data?.claim_id
		});
	}

	searchClaims(params: { search: string; active: boolean }) {
		return this.http.post<any>(environment.api_url + `v1/claim_action/search_claims/`, { ...params });
	}

	checkCatastrophe(param: { claim_type: number; postal_code: number; loss_date: string }) {
		return this.http.post(environment.api_url + `v1/cat_action/check_catastrophe/`, param);
	}

	getExcessDetailsService(data: { claim_id: number }) {
		return this.http.post<any>(`${environment.api_url}v1/claim_action/get_excess_details/`, data);
	}

	// INTERNAL ASSESSOR SERVICES

	isInternalAssessorState(state: number) {
		return this.internalAssessorJobStates.includes(state);
	}

	getinternalAssessorJobCard(jobs: any[]) {
		return jobs.find(j => [89, 90, 28, 88, 37, 97]?.includes(j.state));
	}

	internalAssessorJobHasReport(state: number) {
		return state === 97; // internal_assessor_job_report_uploaded;
	}

	getAllFilesApi(jobId?: number, claimId?: number, purpose?: string) {
		const data: any = {};
		if (jobId) {
			data.job_id = jobId;
		}

		if (claimId) {
			data.claim_id = claimId;
		}

		if (purpose) {
			data.purpose = purpose;
		}

		return this.http.post<any>(environment.api_url + 'v1/file_action/get_files', data);
	}

	addAssessmentNoteApi() {
		return this.getSelectedItemTwo().pipe(
			switchMap(itemTwo =>
				this.http.post<any>(environment.api_url + 'v1/job_action/add_assessment_note', {
					job_id: itemTwo?.id,
					note: this.bf.bigForm.get('ia_message')?.value
				})
			)
		);
	}

	getjobAssessmentNotesApi(job_id: number) {
		const data = { job_id, job_only: true };
		return this.http.post<any>(environment.api_url + 'v1/job_action/get_assessment_notes', data);
	}

	getAssessmentNotesForAllJobsApi(job_id: number) {
		const data = { job_id, job_only: false };
		return this.http.post<any>(environment.api_url + 'v1/job_action/get_assessment_notes', data);
	}

	getJobNotesApi(job_id: number) {
		const data = { job_id };
		return this.http.post<any>(environment.api_url + 'v1/job_action/get_notes', data);
	}

	getAuthorizers() {
		return this.http.get(environment.api_url + 'v1/staff_action/get_authorizers').pipe(map(response => response['payload']));
	}

	// ----------------------------- AVS and Branch look up services -----------------------------
	getAvsCheckBUI(avsData: any) {
		return this.http.post<any>(`${environment.api_url}v1/claim_action/check_account_details/`, avsData);
	}

	getBranchLookupResults(params: { bank: string; branch: string }) {
		return this.http.post<any>(`${environment.api_url}v1/claim_action/get_branch_code/`, { ...params });
	}

	getSPReplyListData() {
		return forkJoin([
			this.store.select(getSelectedItemTwo).pipe(
				filter(Boolean),
				take(1)
			),
			this.store.select(getCurrentUser).pipe(
				filter(Boolean),
				take(1)
			),
			this.store.select(getAllInfo).pipe(
				take(1),
				map((allInfo: any) => allInfo.sps),
				map((sps: any[]) => sps?.reduce((acc, sp: any) => ({ ...acc, [sp.id]: sp }), {}))
			),
			this.store.select(getSelectedItemTwo).pipe(
				filter(Boolean),
				// pluck('id'),
				take(1),
				map((data: any) => data.id),
				switchMap(jobId =>
					this.http.post(`${environment.api_url}v1/job_action/get_interested_parties/`, {
						job_id: jobId
					})
				),
				map((res: any) => res?.payload)
			)
		]).pipe(
			map(([job, user, sps, parties]: any) => {
				if (!!job && !!user && !!sps && !!parties) {
					const isTeamLeader = user?.user?.is_team_leader === true;
					const templateTitle = `The Currently assigned SP is : ${job.sp ? sps[job.sp]?.name : 'none'}`;
					const listConfig = { alwaysEmitEvent: true };

					const entityCardState = {
						'0': { state: 'entity-card--default', blurb: 'Ignored' },
						'-1': { state: 'entity-card--danger', blurb: 'Declined' },
						'1': { state: 'entity-card--success', blurb: 'Accepted' },
						null: { state: 'entity-card--default', blurb: 'No-Reply' },
						currentSP: { state: 'entity-card--info', blurb: 'Current SP' }
					};
					const responseOrderWeightMap = {
						'No-Reply': 3,
						Accepted: 2,
						Ignored: 4,
						Declined: 5,
						'Current SP': 0
					};

					const data: any[] = parties
						.map((party: any) => {
							let lookupValue = party.new_interested;
							lookupValue = job && job?.sp === party.sp.id ? 'currentSP' : lookupValue;

							const cardStateInfo = entityCardState[lookupValue];
							return {
								entityId: party.sp.id,
								cardStateInfo,
								// add a random value, so we can sort randomly
								sort: Math.random(),
								awarded_today: party.awarded_today,
								columns: [
									{
										title: party.sp.name,
										subTitle: party.sp.contact_person,
										description: party.sp.contact_primary
									},
									{
										title: cardStateInfo.blurb,
										subTitle: `${party.sp.mid ? 'On Maven' : 'Not on Maven'}`,
										description: `Number of assignments: ${party.awarded_today}`
									}
								]
							};
						})
						// sort the array by the random values
						.sort((a, b) => a.sort - b.sort)
						.sort((a, b) => responseOrderWeightMap[a.cardStateInfo.blurb] - responseOrderWeightMap[b.cardStateInfo.blurb]);

					const accepted = data?.filter(x => x.cardStateInfo.blurb === 'Accepted');
					const noneAwarded = data?.filter(x => (x.awarded_today === 0 || x.cardStateInfo.blurb !== 'No-Reply') && x.cardStateInfo.blurb !== 'Accepted');
					const awarded = data?.filter(x => x.awarded_today > 0 && x.cardStateInfo.blurb !== 'Accepted');

					const orderedData = [...accepted, ...noneAwarded, ...awarded];

					return {
						isTeamLeader,
						templateTitle,
						listConfig,
						cardData: orderedData || [],
						loadingMessage: 'loading',
						noCardsMessage: 'There are no records to display'
					};
				}
				return {
					templateTitle: '',
					cardData: []
				};
			})
		);
	}

	retryOnMaven() {
		return this.getSelectedItemTwo().pipe(
			switchMap(itemTwo => {
				return this.http.post(`${environment.api_url}v1/job_action/retry_upload_invoice_to_maven/`, { job_id: itemTwo?.id }).pipe(
					filter(x => !!x),
					take(1)
				);
			})
		);
	}

	getCallLogs() {
		return this.getSelectedItemOne().pipe(
			switchMap((itemOne: any) => {
				return this.http.post(`${environment.api_url}v1/claim_action/get_call_logs/`, { claim_id: itemOne?.id }).pipe(
					filter((x: any) => !!x),
					map((data: any) => {
						const arr: any[] = [];
						data?.payload.forEach((log: any) => {
							const formattedLog = transformCallLog(log);
							arr.push(formattedLog);
						});

						return arr;
					})
				);
			})
		);
	}

	addCallLog() {
		return this.getSelectedItemOne().pipe(
			switchMap(itemOne => {
				const data = {
					claim_id: itemOne?.id,
					message: this.bf.bigForm.get('message')?.value,
					channel: this.bf.bigForm.get('channel')?.value,
					reason: this.bf.bigForm.get('reason')?.value,
					direction: this.bf.bigForm.get('direction')?.value
				};
				return this.http.post(`${environment.api_url}v1/claim_action/add_call_log/`, { ...data }).pipe(
					switchMap((x: any) => {
						if (x.success) {
							const addedLog = transformCallLog(x.payload);

							this.bf.bigForm.patchValue({
								message: '',
								channel: null,
								reason: null,
								direction: null
							});

							return this.store.select(getData).pipe(
								map((contextData: any) => [...[addedLog], ...contextData.logs]),
								take(1)
							);
						}
					})
				);
			})
		);
	}

	getClaimNotes() {
		return this.getSelectedItemOne().pipe(
			switchMap(itemOne => {
				return this.getClaimNotesApi(itemOne.id);
			})
		);
	}

	getClaimNotesApi(claim_id: number) {
		const data = { claim_id };
		return this.http.post<any>(environment.api_url + 'v1/claim_action/get_notes', data).pipe(
			filter(x => !!x),
			map((notes: any) => {
				const arr = [];
				notes.payload.forEach(note => {
					note.date = moment(note['timestamp']).format(moment.HTML5_FMT.DATE);
					note.time = moment(note['timestamp']).format(moment.HTML5_FMT.TIME);
					arr.push(note);
				});
				return arr;
			})
		);
	}

	addJobNote() {
		return this.getSelectedItemTwo().pipe(
			switchMap(itemTwo => {
				const data = {
					job_id: itemTwo?.id,
					message: this.bf.bigForm.get('message')?.value
				};
				return this.http.post(`${environment.api_url}v1/job_action/add_note/`, { ...data }).pipe(map(response => response['payload']));
			})
		);
	}

	addClaimNote() {
		return this.getSelectedItemOne().pipe(
			switchMap(itemOne => {
				const data = {
					claim_id: itemOne?.id,
					message: this.bf.bigForm.get('message')?.value
				};

				return this.http.post(`${environment.api_url}v1/claim_action/add_note/`, { ...data }).pipe(map(response => response['payload']));
			})
		);
	}

	updateJobNewState() {
		return forkJoin([
			this.getSelectedItemTwo().pipe(
				// pluck('id'),
				filter(Boolean),	
				take(1),
				map((data: any) => data.id)
			),
			this.store.select(getSubmissionData).pipe(
				// pluck('new_state'),
				filter(Boolean),
				take(1),
				map((data: any) => data.new_state)
			)
		]).pipe(
			switchMap(([job_id, new_state]) => {
				return this.jobService.updateJob({ job_id: job_id, new_state: new_state });
			})
		);
	}

	assignAssessor() {
		return forkJoin([
			this.getSelectedItemTwo().pipe(
				// pluck('id'),
				filter(Boolean),
				take(1),
				map((data: any) => data.id)
			),
			this.store.select(getSubmissionData).pipe(
				// pluck('assign_assessor_id'),
				filter(Boolean),
				take(1),
				map((data: any) => data.assessor_id)
			)
		]).pipe(
			switchMap(([jobId, assessorId]) => {
				return this.jobService.assignAssessor({ job_id: jobId, assessor_id: assessorId });
			})
		);
	}

	AssessorReportUploadedUpdateJob() {
		return this.store.select(getFullItemTwo).pipe(
			filter(Boolean),
			take(1),
			switchMap((job: any) => {
				let { job_information } = job;
				let newState, return_to_state;

				switch (job.state) {
					case 89:
						newState = 97;
						break;
					case 90:
					case 32:
					case 106:
						newState = job?.state;
						return_to_state = 97;
						break;
					default:
						newState = job?.state;
						break;
				}

				const data = {
					job_id: job?.id,
					current_state: job?.state,
					new_state: newState
				};

				let toReturn;
				if (return_to_state) {
					if (job_information) {
						inlineRecursiveMerge(job_information, { return_to_state });
					} else {
						job_information = { return_to_state };
					}

					toReturn = { job_information, ...data };
				} else {
					toReturn = data;
				}
				return this.jobService.updateJob(toReturn);
			})
		);
	}

	changeJobAppointmentFromContextMenu() {
		const appointment = this.bf.bigForm.get('appointmentData')?.value;
		let range_start: moment.Moment;
		let range_end: moment.Moment;

		if (appointment.appointmentTime === 3) {
			range_start = moment(appointment.appointmentDatePicker).set({
				hour: appointment.range_start.hour,
				minute: appointment.range_start.minutes
			});

			range_end = moment(appointment.appointmentDatePicker).set({
				hour: appointment.range_end.hour,
				minute: appointment.range_end.minutes
			});
		} else {
			range_start = moment(appointment.appointmentDatePicker).set({
				hour: appointment.appointmentTimePicker.hour,
				minute: appointment.appointmentTimePicker.minutes
			});
		}

		const appointment_formatted: any = {
			range_start: range_start.format('YYYY-MM-DDTHH:mm:ss'),
			appointment_type: appointment.appointmentTime,
			reason: ''
		};

		if (range_end) {
			appointment_formatted.range_start = range_start.format('YYYY-MM-DDTHH:mm:ss');
			appointment_formatted.range_end = range_end.format('YYYY-MM-DDTHH:mm:ss');
		}

		return forkJoin([this.store.pipe(take(1)), this.store.select(getFullItemTwo).pipe(take(1))]).pipe(
			switchMap(([store, fullItemTwo]) => {
				const office_use = {
					...fullItemTwo.office_use,
					appointment_type: appointment.appointmentTime ? getAllInfoIndex('appointment_types', 'id', 'name', appointment.appointmentTime, store) : null,
					appointment_type_id: appointment.appointmentTime,
					appointmentdatetype: appointment.appointmentDateType,
					requestedappointmentdate: moment(appointment_formatted.range_start).format('YYYY-MM-DD'),
					requestedappointmenttime: moment(appointment_formatted.range_start).format('HH:mm')
				};

				return this.jobService
					.changeAppointment({
						appointment: appointment_formatted,
						job_id: fullItemTwo.id,
						job_information: { ...fullItemTwo.job_information, return_to_state: fullItemTwo.state },
						office_use
					})
					.pipe(take(1));
			})
		);
	}

	recommendCil() {
		return this.store
			.select(getSubmissionData)
			.pipe(take(1))
			.pipe(
				switchMap(submissionData => {
					const { cil_state, effected_jobs, job_id } = submissionData;
					const data = { cil_state, effected_jobs, job_id };
					return this.http.post<any>(environment.api_url + 'v1/job_action/move_to_cil/', data);
				})
			);
	}

	getClaimsHandlers() {
		return this.http.get(environment.api_url + 'v1/staff_action/get_claim_handler', { params: { roles: '1' } }).pipe(
			take(1),
			pluck('payload'),
			map((arr: any) => arr.filter(x => x.authorised === true)),
			map((obj: { [key: string]: { id: number; full_name: string; is_team_leader: boolean } }) => {
				return Object.values(obj).map(entry => ({
					display: entry.full_name,
					value: entry.id,
					is_team_leader: entry.is_team_leader
				}));
			})
		);
	}

	changeClaimHandler() {
		return this.store
			.select(getSubmissionData)
			.pipe(take(1))
			.pipe(
				switchMap(submissionData => {
					const { claim_id, reason, staff_id } = submissionData;
					const data = { claim_id, reason, staff_id };
					return this.http.post<any>(environment.api_url + 'v1/claim_action/change_claim_handler/', data);
				})
			);
	}

	setJobAppointment() {
		const appointment = this.bf.bigForm.get('appointmentData')?.value;
		const date = moment(appointment.appointmentDatePicker);
		date.hour(appointment.appointmentTimePicker.hour);
		date.minutes(appointment.appointmentTimePicker.minutes);

		const date_formatted = date.format('YYYY-MM-DDTHH:mm:ss');
		const appointment_formatted = {
			range_start: date_formatted,
			appointment_type: appointment.appointmentTime,
			reason: ''
		};

		return this.store.select(getFullItemTwo).pipe(
			take(1),
			switchMap(fullItemTwo => {
				let new_state;
				switch (fullItemTwo.state) {
					case 132: {
						new_state = 77;
						break;
					}
				}
				return this.jobService.updateJob({
					current_state: fullItemTwo.state,
					new_state,
					appointment: appointment_formatted,
					job_id: fullItemTwo.id,
					job_information: { ...fullItemTwo.job_information, return_to_state: fullItemTwo.state }
				});
			})
		);
	}

	// Supporting Document Upload Methods

	/**
	 * Get all stored supporting documents from local storage
	 */
	getStoredSupportingDocuments(): Observable<StoredDocument[]> {
		return from(
			(async () => {
				const documents: StoredDocument[] = [];
				const config = this.bf.bigForm.get('tenant_document_config')?.value;
				const requiredDocs = config?.required_documents || [];

				for (const doc of requiredDocs) {
					const key = `supporting_doc_${doc.type}`;
					try {
						// Try localStorage first
						const stored = localStorage.getItem(key);
						if (stored) {
							const parsed = JSON.parse(stored);
							documents.push(parsed);
							continue;
						}

						// Try IndexedDB
						const indexedDoc = await this.indexedDbService.data_store.get(key);
						if (indexedDoc?.data) {
							documents.push(indexedDoc.data);
						}
					} catch (error) {
						console.error(`Error retrieving document ${key}:`, error);
					}
				}

				return documents;
			})()
		);
	}

	/**
	 * Remove stored document from local storage
	 */
	private removeStoredDocument(documentType: string): Observable<void> {
		return from(
			(async () => {
				const key = `supporting_doc_${documentType}`;
				try {
					localStorage.removeItem(key);
					await this.indexedDbService.data_store.delete(key);
				} catch (error) {
					console.error('Error removing stored document:', error);
				}
			})()
		);
	}

	/**
	 * Clear all supporting documents from local storage and IndexedDB
	 * This should be called when navigating away from the claim creation flow
	 * This method ALWAYS clears ALL supporting documents, regardless of config availability
	 * Sets a flag to prevent clearStoredDocumentsForTypes from preserving documents on fresh start
	 */
	clearAllSupportingDocuments(): Observable<void> {
		return from(
			(async () => {
				try {
					console.log('🧹 clearAllSupportingDocuments: Starting cleanup...');
					
					// Set a flag to indicate we're doing a full cleanup
					// This prevents clearStoredDocumentsForTypes from preserving documents
					localStorage.setItem('_pgg_clear_all_docs_flag', Date.now().toString());
					localStorage.removeItem('supporting_docs_metadata');
					
					// First, try to clear documents based on config if available (for efficiency)
					let requiredDocs: any[] = [];
					try {
						const config = this.bf?.bigForm?.get('tenant_document_config')?.value;
						requiredDocs = config?.required_documents || [];
					} catch (error) {
						console.warn('🧹 clearAllSupportingDocuments: Could not access form config, will clear all documents');
					}

					if (requiredDocs.length > 0) {
						console.log('🧹 clearAllSupportingDocuments: Clearing', requiredDocs.length, 'documents from config');
						for (const doc of requiredDocs) {
							const key = `supporting_doc_${doc.type}`;
							try {
								// Remove from localStorage
								localStorage.removeItem(key);
								// Remove from IndexedDB
								await this.indexedDbService.data_store.delete(key);
								console.log('🧹 Cleared document:', key);
							} catch (error) {
								console.error(`Error removing document ${key}:`, error);
							}
						}
					}

					// ALWAYS clear ALL supporting documents from IndexedDB (safety net)
					// This ensures we catch everything, even if config is missing or incomplete
					try {
						console.log('🧹 clearAllSupportingDocuments: Scanning IndexedDB for all supporting_doc_ keys...');
						const allKeys = await this.indexedDbService.data_store.toCollection().keys();
						let clearedCount = 0;
						
						for (const key of allKeys) {
							if (typeof key === 'string' && key.startsWith('supporting_doc_')) {
								try {
									await this.indexedDbService.data_store.delete(key);
									// Also clear from localStorage for backward compatibility
									localStorage.removeItem(key);
									clearedCount++;
									console.log('🧹 Cleared document from IndexedDB:', key);
								} catch (error) {
									console.error(`Error clearing document ${key} from IndexedDB:`, error);
								}
							}
						}
						
						console.log('🧹 clearAllSupportingDocuments: Cleared', clearedCount, 'documents from IndexedDB');
					} catch (error) {
						console.error('🧹 Error scanning/clearing supporting documents from IndexedDB:', error);
					}
					
					console.log('🧹 clearAllSupportingDocuments: Cleanup complete');
				} catch (error) {
					console.error('🧹 Error in clearAllSupportingDocuments:', error);
				}
			})()
		);
	}

	/**
	 * Upload a single document with progress tracking
	 */
	private uploadDocument(
		doc: StoredDocument,
		jobId: number,
		progressCallback: (progress: UploadProgress) => void
	): Observable<any> {
		const progress: UploadProgress = {
			documentType: doc.documentType,
			documentLabel: doc.documentLabel,
			status: 'uploading',
			progress: 0
		};

		progressCallback(progress);

		// Get file extension
		const getFileExtension = (fileName: string): string => {
			return fileName.split('.').pop()?.toLowerCase() || 'pdf';
		};

		const fileExtension = getFileExtension(doc.file.name);
		const purpose = doc.documentLabel; // Use document label as purpose (unique per document type)

		let metadata = this.bf?.bigForm?.get('extracted_document_data')?.value;

		if (!metadata || Object.keys(metadata).length === 0) {
			const storedMetadata = localStorage.getItem('supporting_docs_metadata');
			if (storedMetadata) {
				try {
					metadata = JSON.parse(storedMetadata);
				} catch (error) {
					console.warn('Could not parse supporting document metadata from storage:', error);
				}
			}
		}

		// Determine meta field based on whether there's AI extracted data
		const hasExtractedData = metadata && Object.keys(metadata).length > 0;
		const meta = hasExtractedData
			? { extracted_document_data: metadata }
			: { ai_analysis: 'Disabled' };

		const body: Record<string, unknown> = {
			data: doc.base64,
			job_id: jobId,
			purpose: purpose, // Unique purpose for each document type
			filename: `${doc.documentType}-${jobId}-${Date.now()}.${fileExtension}`,
			meta: meta
		};
		if (doc.thumbnail) {
			body.thumbnail = this.toRawBase64(doc.thumbnail);
		}

		return this.http.post(`${environment.api_url}v1/file_action/upload_file/`, body, {
			observe: 'events',
			reportProgress: true
		}).pipe(
			map(event => {
				if (event.type === HttpEventType.UploadProgress) {
					if (event.total) {
						progress.progress = Math.round((100 * event.loaded) / event.total);
						progressCallback({ ...progress });
					}
				} else if (event.type === HttpEventType.Response) {
					progress.status = 'success';
					progress.progress = 100;
					progressCallback({ ...progress });
				}
				return event;
			}),
			catchError(error => {
				progress.status = 'error';
				progress.error = error.message || 'Upload failed';
				progressCallback({ ...progress });
				return throwError(() => error);
			})
		);
	}

	private uploadDocumentWithClaimId(
		doc: StoredDocument,
		claimId: number,
		jobId: number | null,
		progressCallback: (progress: UploadProgress) => void
	): Observable<any> {
		const progress: UploadProgress = {
			documentType: doc.documentType,
			documentLabel: doc.documentLabel,
			status: 'uploading',
			progress: 0
		};

		progressCallback(progress);

		const getFileExtension = (fileName: string): string => {
			return fileName.split('.').pop()?.toLowerCase() || 'pdf';
		};

		const fileExtension = getFileExtension(doc.file.name);
		const purpose = doc.documentLabel;

		let metadata = this.bf?.bigForm?.get('extracted_document_data')?.value;
		if (!metadata || Object.keys(metadata).length === 0) {
			const storedMetadata = localStorage.getItem('supporting_docs_metadata');
			if (storedMetadata) {
				try {
					metadata = JSON.parse(storedMetadata);
				} catch (error) {
					console.warn('Could not parse supporting document metadata from storage:', error);
				}
			}
		}

		const hasExtractedData = metadata && Object.keys(metadata).length > 0;
		const meta = hasExtractedData
			? { extracted_document_data: metadata }
			: { ai_analysis: 'Disabled' };

		const body: Record<string, unknown> = {
			data: doc.base64,
			claim_id: claimId,
			purpose,
			filename: `${doc.documentType}-claim-${claimId}-${Date.now()}.${fileExtension}`,
			meta
		};
		if (jobId) {
			body.job_id = jobId;
		}

		if (doc.thumbnail) {
			body.thumbnail = this.toRawBase64(doc.thumbnail);
		}

		return this.http.post(`${environment.api_url}v1/file_action/upload_file/`, body, {
			observe: 'events',
			reportProgress: true
		}).pipe(
			map(event => {
				if (event.type === HttpEventType.UploadProgress) {
					if (event.total) {
						progress.progress = Math.round((100 * event.loaded) / event.total);
						progressCallback({ ...progress });
					}
				} else if (event.type === HttpEventType.Response) {
					progress.status = 'success';
					progress.progress = 100;
					progressCallback({ ...progress });
				}
				return event;
			}),
			catchError(error => {
				progress.status = 'error';
				progress.error = error.message || 'Upload failed';
				progressCallback({ ...progress });
				return throwError(() => error);
			})
		);
	}

	private toRawBase64(value: string): string {
		const trimmed = value.trim();
		// Standard data URL format: data:image/jpeg;base64,<payload>
		const standard = trimmed.match(/^data:[^;]+;base64,(.+)$/i);
		if (standard?.[1]) {
			return standard[1];
		}
		// Malformed format seen in responses: dataimage/jpegbase64<payload>
		const malformed = trimmed.match(/^dataimage\/[a-zA-Z0-9.+-]+base64(.+)$/i);
		if (malformed?.[1]) {
			return malformed[1];
		}
		// Already raw base64
		return trimmed;
	}

	/**
	 * Upload all stored supporting documents after claim creation
	 * Shows progress in a self-dismissing modal
	 */
	uploadSupportingDocuments(): Observable<any> {
		console.log('uploadSupportingDocuments 1451', this.lastCreatedJobId);
		return forkJoin([this.store.select(getSelectedItemTwo).pipe(take(1)), this.store.select(getFullItemOne).pipe(take(1))]).pipe(
			switchMap(([job, fullItemOne]) => {
				const jobId =
					this.toPositiveNumber(job?.id) ??
					this.toPositiveNumber(this.lastCreatedJobId) ??
					this.extractJobIdFromClaim(fullItemOne);

				if (!jobId) {
					return of({ success: true, message: 'No job id available for upload' });
				}
				return this.getStoredSupportingDocuments().pipe(
					switchMap(documents => {
						console.log('uploadSupportingDocuments 1449', documents);
						if (!documents || documents.length === 0) {
							return of({ success: true, message: 'No documents to upload' });
						}

						// Open progress modal
						const progressMap = new Map<string, UploadProgress>();
						let completedCount = 0;
						const totalCount = documents.length;

						this.modalsService.openModalDirectly(inst => {
							inst.type = 'info';
							inst.heading = 'Uploading Documents';
							inst.closeButton = false;
							inst.message = [`Uploading ${totalCount} document(s)...`];

							// Update modal message with progress
							const updateModalMessage = () => {
								const messages: string[] = [`Uploading ${totalCount} document(s)...`];

								progressMap.forEach((progress) => {
									if (progress.status === 'uploading') {
										messages.push(`${progress.documentLabel}: ${progress.progress || 0}%`);
									} else if (progress.status === 'success') {
										messages.push(`✓ ${progress.documentLabel}: Uploaded successfully`);
									} else if (progress.status === 'error') {
										messages.push(`✗ ${progress.documentLabel}: ${progress.error || 'Upload failed'}`);
									}
								});

								inst.setMessage(messages);

								// Auto-dismiss when all uploads complete
								if (completedCount === totalCount) {
									setTimeout(() => {
										const allSuccess = Array.from(progressMap.values()).every(p => p.status === 'success');
										if (allSuccess) {
											inst.setMessage([`All ${totalCount} document(s) uploaded successfully!`]);
											setTimeout(() => {
												inst.closeButton = true;
												inst.navButtons = [
													{
														text: 'Close',
														color: 'primary',
														linkType: 'close'
													}
												];
											}, 1000);
										} else {
											inst.setMessage([`Upload completed with some errors. Please check the details above.`]);
											inst.closeButton = true;
											inst.navButtons = [
												{
													text: 'Close',
													color: 'primary',
													linkType: 'close'
												}
											];
										}
									}, 500);
								}
							};

							// Upload all documents
						const uploadObservables = documents.map(doc => {
							return this.uploadDocument(doc, jobId, (progress) => {
									progressMap.set(doc.documentType, progress);
									updateModalMessage();

									if (progress.status === 'success' || progress.status === 'error') {
										completedCount++;
										updateModalMessage();
									}
								}).pipe(
									catchError(() => {
										completedCount++;
										updateModalMessage();
										return of({ error: true, documentType: doc.documentType });
									})
								);
							});

							// Execute all uploads
							forkJoin(uploadObservables).subscribe({
								next: () => {
									// Clean up stored documents after successful upload
									documents.forEach(doc => {
										this.removeStoredDocument(doc.documentType).subscribe();
									});
								},
								error: () => {
									// Handle any critical errors
								}
							});
						});

						return of({ success: true });
					})
				);
			})
		);
	}

	uploadSupportingDocumentsWithClaimId(claimId?: number, jobId?: number): Observable<any> {
		return forkJoin([this.store.select(getSelectedItemTwo).pipe(take(1)), this.store.select(getFullItemOne).pipe(take(1))]).pipe(
			take(1),
			switchMap(([job, fullItemOne]) => {
				console.log('uploadSupportingDocumentsWithClaimId 1852', claimId, jobId, fullItemOne);
				const resolvedClaimId =
					this.toPositiveNumber(claimId) ??
					this.toPositiveNumber(this.lastCreatedClaimId) ??
					this.toPositiveNumber(fullItemOne?.id);
				const resolvedJobId =
					this.toPositiveNumber(jobId) ??
					this.toPositiveNumber(job?.id) ??
					this.toPositiveNumber(this.lastCreatedJobId) ??
					this.extractJobIdFromClaim(fullItemOne);

				if (!resolvedClaimId) {
					return throwError(() => new Error('No claim id available for document upload'));
				}

				return this.getStoredSupportingDocuments().pipe(
					switchMap(documents => {
						if (!documents || documents.length === 0) {
							return of({
								success: true,
								message: 'No documents to upload',
								claim_id: resolvedClaimId,
								...(resolvedJobId ? { job_id: resolvedJobId } : {})
							});
						}

						const progressMap = new Map<string, UploadProgress>();
						let completedCount = 0;
						const totalCount = documents.length;
						let modalInstance: any;

						this.modalsService.openModalDirectly(inst => {
							modalInstance = inst;
							inst.type = 'info';
							inst.heading = 'Uploading Documents';
							inst.closeButton = false;
							inst.message = [`Uploading ${totalCount} document(s)...`];
						});

						const updateModalMessage = () => {
							if (!modalInstance) {
								return;
							}

							const messages: string[] = [`Uploading ${totalCount} document(s)...`];

							progressMap.forEach((progress) => {
								if (progress.status === 'uploading') {
									messages.push(`${progress.documentLabel}: ${progress.progress || 0}%`);
								} else if (progress.status === 'success') {
									messages.push(`✓ ${progress.documentLabel}: Uploaded successfully`);
								} else if (progress.status === 'error') {
									messages.push(`✗ ${progress.documentLabel}: ${progress.error || 'Upload failed'}`);
								}
							});

							modalInstance.setMessage(messages);

							if (completedCount === totalCount) {
								setTimeout(() => {
									const allSuccess = Array.from(progressMap.values()).every(p => p.status === 'success');
									if (allSuccess) {
										modalInstance.setMessage([`All ${totalCount} document(s) uploaded successfully!`]);
									} else {
										modalInstance.setMessage([`Upload completed with some errors. Please check the details above.`]);
									}

									modalInstance.closeButton = true;
									modalInstance.navButtons = [
										{
											text: 'Close',
											color: 'primary',
											linkType: 'close'
										}
									];
								}, 500);
							}
						};

						const uploadObservables = documents.map(doc => {
							return this.uploadDocumentWithClaimId(doc, resolvedClaimId, resolvedJobId ?? null, (progress) => {
								progressMap.set(doc.documentType, progress);
								updateModalMessage();

								if (progress.status === 'success' || progress.status === 'error') {
									completedCount++;
									updateModalMessage();
								}
							}).pipe(
								map(() => ({ documentType: doc.documentType, success: true })),
								catchError(error => of({ documentType: doc.documentType, success: false, error }))
							);
						});

						return forkJoin(uploadObservables).pipe(
							switchMap(results => {
								const failedDocuments = results.filter(result => !result.success);
								if (failedDocuments.length > 0) {
									return throwError(
										() => new Error(`Failed to upload ${failedDocuments.length} supporting document(s)`)
									);
								}

								return forkJoin(documents.map(doc => this.removeStoredDocument(doc.documentType))).pipe(
									map(() => ({
										success: true,
										claim_id: resolvedClaimId,
										...(resolvedJobId ? { job_id: resolvedJobId } : {}),
										uploadedCount: documents.length
									}))
								);
							})
						);
					})
				);
			})
		);
	}

}
